// src/tours/runTour.js — run a role's tour with driver.js (D10) and record
// it as seen on the backend. Only the steps whose anchor is on the current
// page are shown (the tour runs on the role's home page; the sidebar items
// are on every page). driver.js gives Esc to skip, ← / → to move, and keeps
// focus inside the popover.
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import api from '../services/api';
import { tourKey, tourSteps, TOURS } from './tours';

export function shouldStartTour(role, seenRows = []) {
  if (!TOURS[role]) return false;
  const key = tourKey(role);
  return !seenRows.some((r) => r.key === key);
}

export async function markTourSeen(role) {
  try { await api.post(`/accounts/me/tours/${tourKey(role)}/seen/`); } catch { /* retried next visit */ }
}

// An anchor counts only when it is on screen: below lg the sidebar is a
// drawer translated off-screen, its links still in the DOM — a step there
// would spotlight nothing the user can see. (An element with no box at all
// is kept: jsdom and hidden-until-measured layouts report zeros.)
export function isOnScreen(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (!r || r.width === 0) return true;
  return !(r.right <= 0 || r.left >= (window.innerWidth || 0) || r.bottom <= 0);
}

// The progress strip (P1, v1.4.0): one segment per step, the ones up to the
// current step filled — read out as "Step 3 of 7".
export function progressSegments(index, total) {
  return Array.from({ length: total }, (_, i) => i <= index);
}
export const progressLabel = (index, total) => `Step ${index + 1} of ${total}`;

export function renderProgress(popover, index, total) {
  const wrapper = popover?.wrapper;
  if (!wrapper || total < 2) return;
  wrapper.querySelector('.lpms-tour-progress')?.remove();
  const strip = document.createElement('div');
  strip.className = 'lpms-tour-progress';
  strip.setAttribute('role', 'progressbar');
  strip.setAttribute('aria-label', 'Tour progress');
  strip.setAttribute('aria-valuemin', '1');
  strip.setAttribute('aria-valuemax', String(total));
  strip.setAttribute('aria-valuenow', String(index + 1));
  strip.setAttribute('aria-valuetext', progressLabel(index, total));
  progressSegments(index, total).forEach((filled) => {
    const seg = document.createElement('span');
    if (filled) seg.className = 'is-filled';
    strip.appendChild(seg);
  });
  wrapper.insertBefore(strip, popover.title || wrapper.firstChild);
}

export function prefersReducedMotion() {
  try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// v1.4.1 C7: the running tour, so the idle warning can end it (driver.js
// blocks clicks everywhere else while a tour runs). A tour stopped that way
// is NOT marked seen — it shows again next time.
let activeTour = null;
let stoppedForIdle = false;
export function stopActiveTour() {
  if (!activeTour) return false;
  stoppedForIdle = true;
  try { activeTour.destroy(); } catch { /* already gone */ }
  return true;
}

export function startTour(role, { brandName, modulesAvailable, onDone } = {}) {
  const steps = tourSteps(role, { brandName, modulesAvailable }).filter((s) => isOnScreen(document.querySelector(s.element)));
  if (!steps.length) return null;
  const tour = driver({
    steps,
    showProgress: false,          // the strip below replaces "6 of 7"
    popoverClass: 'lpms-tour',
    animate: !prefersReducedMotion(),
    overlayColor: '#081A14',      // mti dark, softly
    overlayOpacity: 0.45,
    stagePadding: 8,
    stageRadius: 12,
    allowClose: true,
    allowKeyboardControl: true,
    showButtons: ['next', 'previous', 'close'],
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    onPopoverRender: (popover, { state }) => renderProgress(popover, state?.activeIndex || 0, steps.length),
    onDestroyed: () => {
      activeTour = null;
      const forIdle = stoppedForIdle;
      stoppedForIdle = false;
      if (forIdle) return;          // not seen, and nothing offered after it (What's new)
      markTourSeen(role);
      onDone?.();
    },
  });
  activeTour = tour;
  tour.drive();
  return tour;
}
